import type { LookupUser, LookupUsers } from '@atlassian/ui-modifications-core/types';
import { FieldValueLookupError, isNotAutomaticId } from '@atlassian/ui-modifications-core/utils';
import type { MultiUserPickerPublicGetValue } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/multi-user-picker.tsx';
import { getValueLookupValuesDiff } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/get-value-lookup-values-diff/index.tsx';
import { isNotEmptyString } from '@atlassian/ui-modifications-public-api-utils/is-not-empty-string';
import { multiValueLookup } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/multi-value-lookup/index.tsx';

type InternalValue = {
	id: string;
	name: string;
	avatarUrl?: string;
};

export type MultiUserPickerPublicSetValue = string[];

export type MultiUserPickerInternalValue = InternalValue[];

export const multiUserPickerSetValueValidator = (
	value: unknown,
): value is MultiUserPickerPublicGetValue =>
	Array.isArray(value) &&
	// We should omit automatic id special value so it won't be picked up by this field
	// It is only applicable to the assignee field
	value.every((option) => isNotEmptyString(option) && isNotAutomaticId(option));

export const multiUserPickerSetValueTransformer = (
	values: MultiUserPickerPublicSetValue,
	lookupValues?: LookupUsers,
): MultiUserPickerInternalValue => {
	if (!values.length) {
		return [];
	}

	const filteredLookupValues = multiValueLookup<LookupUser>(values, lookupValues, 'accountId');

	if (values.length !== filteredLookupValues.length) {
		const notFoundValues = getValueLookupValuesDiff(values, filteredLookupValues, 'accountId');

		throw new FieldValueLookupError(notFoundValues.join());
	}

	return filteredLookupValues.map(({ accountId: id, name, picture: avatarUrl }) => ({
		id,
		name,
		avatarUrl,
	}));
};

export const multiUserPickerGetValueTransformer = (
	value: MultiUserPickerInternalValue,
): MultiUserPickerPublicGetValue => {
	if (!value) {
		return [];
	}

	return value.map(({ id }) => ({ accountId: id }));
};
